import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import classes from './MainContainer.module.scss';
import Header from '../../components/Header/Header';
//import PrechatForm from '../PrechatForm/PrechatForm';
import FileUploadModal from '../../components/FileUploadModal/FileUploadModal';
import FeedbackModal from '../../components/FeedbackModal/FeedbackModal';
import EndChatModal from '../../components/EndChatModal/EndChatModal';
import ErrorModal from '../../components/ErrorModal/ErrorModal';
import MessageComposer from '../../components/MessageComposer/MessageComposer';
import MessageList from '../MessageList/MessageList';
import Typing from '../Typing/Typing';
import QuickResponseContainer from '../QuickResponseContainer/QuickResponseContainer';
import { connect, disconnect, refresh, fileuploadConfig } from '../../api/Connect/Connect';
import Message from '../../models/Message';
import QuickResponse from '../../models/QuickResponse';
import Alert from "../../components/Alert/Alert";
import { addMessage, setQuickResponses, setIsTyping, setMode, setShowUpload, setMessagesLoaded } from '../../store/actions/messages';
import { setResetChat, setAgentName, setShowFeedback } from '../../store/actions/general';
import { initiateGenesysSession, refreshGenesysSession, disconnectGenesysSession, setFileConfig, setApiError } from '../../store/actions/genesys';
import { useDispatch, useSelector } from 'react-redux';
import { getComponentsConfig, getGenesysConfig, shouldRestartChat, setRestartChat, setChatEnded, isChatEnded } from '../../utils/config';
import type { RootState, GenesysApiResponse, QuickReplyContent } from '../../types/redux';

const MainContainer = () => {
  const socketRef = useRef(null);
  const components = getComponentsConfig();
  const { prechatEnabled, widgetIsOpen, resetChat, showFeedback, prechatStatus } = useSelector((state: RootState) => state.general);
  const { chatId, alias, secureKey, userId, nextPosition, chatEnded, fileConfig, hasApiError } = useSelector((state: RootState) => state.genesys);
  const { messages, typing, mode, showUpload, messagesLoaded } = useSelector((state: RootState) => state.messages);
  const [pingTime, setPingTime] = useState(null)
  const [refreshRunning, setRefreshRunning] = useState(false);
  const [fileUploadConfigRunning, setFileUploadConfigRunning] = useState(false);
  const messageList = useRef(null);
  const messageListBottom = useRef(null);
  const hadIdleWarning = useRef(false);
  // eslint-disable-next-line 
  const [fileuploadEnabled, setFileuploadEnabled] = useState(components["fileupload"]["enabled"])
  // eslint-disable-next-line 
  const [feedbackEnabled, setFeedbackEnabled] = useState(components["feedback"]["enabled"])
  const dispatch = useDispatch();
  
  const scrollToBottom = useCallback((shouldSmooth) => {
    if (shouldSmooth) {
      messageListBottom.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      messageList.current.scrollTop = messageList.current.scrollHeight;
    }
  }, []);

  const handleCloseUpload = () => {
    dispatch(setShowUpload(false));
  };

  const handleCloseFeedbackModal = () => {
    dispatch(setShowFeedback(false));
  };

  const handleCloseEndchatModal = () => {
    dispatch(setResetChat(false))
  }

  useEffect(() => {
    const handleChatEnd = (reasonText?: string) => {
      if (!shouldRestartChat()) {
        if (!isChatEnded()) {
          const closedDueToInactivity = reasonText || (hadIdleWarning.current ? "Chat session was closed due to inactivity." : null);
          if (closedDueToInactivity) {
            dispatch(addMessage(new Message({
              incoming: true,
              isSystem: true,
              isSystemBottom: true,
              text: closedDueToInactivity,
            })));
          } else {
            dispatch(addMessage(new Message({
              incoming: true,
              isSystem: true,
              isSystemBottom: true,
              text: "Chat session has been closed.",
            })));
          }
        }
        // De-dup the "Chat Ended" divider so it only renders once.
        if (!isChatEnded()) {
          dispatch(addMessage(new Message({
            incoming: true,
            isSystem: true,
            text: "Chat Ended",
            isSystemBottom: false
          })));
          dispatch(setMessagesLoaded(false))
        }
      }
      setChatEnded(true);
      setRestartChat(false);
      dispatch(setQuickResponses([]));
      if (mode === 'agent') dispatch(setShowFeedback(true));
    }

    const generateMessages = (messages, isInitial) => {
      // Participant ID:
      // 1 - user
      // 2 - Bot
      // 3 - Agent
      // 4 - System
      for (const message of messages) {
        const messageFrom = !message.from ? {} : message.from;
        const fromType = messageFrom.type ? messageFrom.type.toLowerCase() : ''

        if (fromType === 'agent' && messageFrom.participantId >= 3 && message.type !== 'ParticipantJoined') {
          const nickname = !messageFrom.nickname ? 'Anonymous' : messageFrom.nickname;
          message.isAgent = true;
          dispatch(setAgentName(nickname))
          dispatch(setMode('agent'))
        }
        switch(message.type) {
          case "ParticipantJoined":
            if (['agent', 'client'].includes(fromType)) {
              message.incoming = true;
              message.isSystem = true;
              if (isInitial) {
                message.text = "Preparing Chat";
                message.isSystemBottom = false;
                dispatch(addMessage(new Message(message)));
              } else {
                if (messageFrom.participantId >= 3) {
                  message.text = `${messageFrom.nickname} connected`;
                } else {
                  message.text = 'Chat Started';
                }
                message.isSystemBottom = true;
                dispatch(addMessage(new Message(message)));
              }
            }
            break;
          case "Message":
            message.incoming = true;
            if (message.bodyType === 'notice') {
              dispatch(addMessage(new Message({
                incoming: true,
                isSystem: true,
                isSystemBottom: true,
                isWarning: true,
                text: message.text,
              })));
              break;
            }
            if ((message.messageType === 'text' || message.messageType === "Message" || message.isAgent) && fromType === 'agent') {
              const isEndChatTrigger = message.text.includes('[endchattrigger]: #');

              dispatch(addMessage(new Message(message)));
              if (!messagesLoaded) {
                const keyword_exclude = [" .", ".", "Greetings!"];
                if (!keyword_exclude.includes(message.text)) {
                  dispatch(setMessagesLoaded(true))
                }
              }
              
              if (isEndChatTrigger) {
                setTimeout(() => {
                  handleChatEnd();
                  dispatch(setResetChat(false))
                  dispatch(disconnectGenesysSession());
                  disconnect({
                      chatId,
                      alias,
                      secureKey,
                      userId,
                      chatEnded
                  }, getGenesysConfig());
                }, 2000)
              } else {
                // Check Quick Replies
                const content = message?.eventAttributes?.['structured-content']?.['genesys-chat']?.['content'];
                let quickReplies: QuickReplyContent | string = '';
                
                try {
                  quickReplies = JSON.parse(content);
                } catch {
                  quickReplies = '';
                }
                
                if (quickReplies !== '') {
                  if (typeof quickReplies === 'object' && 'content' in quickReplies && Array.isArray(quickReplies.content) && quickReplies.contentType === 'quick-replies') {
                    const responsesList = [];
                    for (const option of quickReplies.content) {
                      option['eventAttributes'] = {
                        "general-properties": {
                          "related-event-id": message.index
                        }
                      }
                      const newQuickResponse = new QuickResponse(option);
                      responsesList.push(newQuickResponse);
                    }
                    dispatch(setQuickResponses(responsesList));
                  }
                }
              }
            } else if (message.type === "Message" && fromType === 'external') {
              message.isSystem = true;
              message.isSystemBottom = true;
              dispatch(addMessage(new Message(message)));
            }
            dispatch(setIsTyping(false))
            break;
          case "TypingStarted":
            if (fromType === 'agent') {
              dispatch(setIsTyping(true))
            }
            break;
          case "TypingStopped":
            if (fromType === 'agent') {
              dispatch(setIsTyping(false))
            }
            break;
          case "FileUploaded":
            message.incoming = fromType === 'agent';
            message.isFileMessage = true;
            message.fileData = message["userData"];
            message.mode = fromType === 'agent' ? 'agent' : 'user';
            message.type = 'file-message';
            dispatch(addMessage(new Message(message)));
            break;
          case "Notice":
          case "IdleAlert":
          case "IdleClose":
            if (!hadIdleWarning.current) {
              hadIdleWarning.current = true;
              dispatch(addMessage(new Message({
                incoming: true,
                isSystem: true,
                isSystemBottom: true,
                isWarning: true,
                text:  "Chat session will be closed soon due to inactivity.",
              })));
            }
            break;
          case "ParticipantLeft":
            if (fromType === 'client') {
              const quitCode = message.eventAttributes?.['GCTI_SYSTEM']?.['quit-reason-code'];
              const reasonText: Record<string, string> = {
                "0": "Chat session has been closed.",
                "3": "Chat session was closed due to inactivity.",
                "4": "Chat session has been closed.",
                "5": "Chat session has been closed.",
                "7": "Chat session was closed due to inactivity.",
              };
              handleChatEnd(reasonText[quitCode]);
            }
            break;
          default:
            break;
        }
      }
    }
    
    const getfileUploadConfig = () => {
      if (!fileConfig && !fileUploadConfigRunning && chatId) {
        setFileUploadConfigRunning(true);
        fileuploadConfig({
          chatId, 
          alias,
          secureKey, 
          userId
        }, getGenesysConfig())
          .then((data: GenesysApiResponse) => {
            const messageInfo = data.messages[0] ? data.messages[0] : {}
            const configData = messageInfo.hasOwnProperty('userData') ? data.messages[0]['userData'] : {};
            dispatch(setFileConfig(configData));
            setFileUploadConfigRunning(false);
          })
      }
    }

    dispatch(setApiError(false));
    // if(!prechatStatus) {
      if (!chatId) {
        setTimeout(() => {
          connect(getGenesysConfig())
            .then((data: GenesysApiResponse) => {
              setChatEnded(false);
              hadIdleWarning.current = false;

              dispatch(initiateGenesysSession(data));
              setPingTime(new Date());
              generateMessages(data.messages, true);
              getfileUploadConfig();
              setRefreshRunning(false);

              setRestartChat(false);
            })
            .catch((error) => {
              console.log('Genesys Error', error)
              dispatch(setApiError(true));
            })
        }, 1000)
      } else if (!refreshRunning && !chatEnded) {
        setRefreshRunning(true);
        setTimeout(() => {
          if (!shouldRestartChat() && !isChatEnded()) {
            refresh({
                chatId, 
                alias, 
                secureKey,
                userId,
                nextPosition,
                chatEnded
              }, getGenesysConfig())
                .then((data: GenesysApiResponse) => {
                  dispatch(refreshGenesysSession(data));
                  setPingTime(new Date());
                  generateMessages(data.messages, false);
                  setRefreshRunning(false);
                  getfileUploadConfig();
                  if (data.chatEnded) {
                    handleChatEnd();
                  }
                })
                .catch((error) => {
                  console.log('Genesys Error', error)
                  dispatch(setApiError(true));
                })
          } else if (isChatEnded()) {
            handleChatEnd();
          }
        }, 1000)
      }
    // }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, chatId, prechatStatus, alias, secureKey, userId, nextPosition, pingTime, messages, messagesLoaded, chatEnded, resetChat, refreshRunning, fileConfig, fileUploadConfigRunning, scrollToBottom]);

  return (
    <div className={prechatEnabled || widgetIsOpen ? classes.MainContainer : classes.MainContainerGrowIn}>
      <div className={classes.MainContainerOuter}>
      <Header/>
      {/* {prechatStatus && <PrechatForm isOpen={true} />}
      {!prechatStatus && */}
        <> 
          <Alert socket={socketRef.current}/>
          <FileUploadModal isOpen={showUpload && fileuploadEnabled} closeHandler={handleCloseUpload} genesysConfig={getGenesysConfig()} />
          <FeedbackModal isOpen={showFeedback && feedbackEnabled} closeHandler={handleCloseFeedbackModal} />
          <EndChatModal isOpen={resetChat} closeHandler={handleCloseEndchatModal} />
          <ErrorModal isOpen={hasApiError} closeHandler={() => {}} />
          <div
            className={classes.content}
            style={{ overflowY: 'auto' }}
            ref={messageList}
          >
            <MessageList scrollToBottom={scrollToBottom} />
            <div ref={messageListBottom}></div>
          </div>

          <QuickResponseContainer/>

          <div className={classes.typeComposerSection}>
            <Typing show={typing} mode="dot-loader" typingText="" />
            {messagesLoaded &&
              <div className={classes.footer}>
                <Suspense fallback={<></>}>
                  <MessageComposer/>
                </Suspense>
              </div>
            }
          </div>
        </>
      {/* } */}
      </div>
    </div>
  );
};

export default MainContainer;